import { string } from 'prop-types';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { buildProducts } from './ProductList';
import ProductChildProduct from './ProductChildProduct';
import { Button, Table } from 'react-bootstrap';
import { CartContext } from './context/CartContext';

export default function ProductChildProductsTable({ productId }) {
    const {
        cartState,
        checkoutData,
        cartUpdating,
        addLineItem,
        setLineItem,
        delLineItem,
        reloadLineItems,
        fetchCheckoutData,
    } = useContext(CartContext);

    const [addedProducts, setAddedProducts] = useState([]);
    const [childProducts, setChildProducts] = useState([]);
    const [loadingChildProducts, setLoadingChildProducts] = useState(true);

    // Add to cart
    const handleClick = useCallback(async (e) => {
        e.preventDefault();
        if (!addedProducts.length) {
            return;
        }
        try {
            const lineItems = addedProducts.map(({ inventoryLevelId, qty }) => ({
                inventoryLevelId,
                quantity: qty,
            }));
            const response = await addLineItem(null, null, lineItems);
            if (!response.success) {
                throw new Error('Something went wrong.');
            }
            setAddedProducts([]);
        } catch (e) {
            console.error(e);
        }
    }, [addedProducts]);

    // Add or remove a product from the cart
    const handleAddToCart = useCallback((inventoryLevelId, qty) => {
        const addedProduct = addedProducts
            .find((product) => product.inventoryLevelId === inventoryLevelId);
        if (addedProduct) {
            setAddedProducts(addedProducts
                .filter((product) => product.inventoryLevelId !== inventoryLevelId));
        } else {
            setAddedProducts([...addedProducts, { inventoryLevelId,
                qty }]);
        }
    }, [addedProducts]);

    // Fetch child products
    const fetchChildProducts = useCallback(async () => {
        try {
            setLoadingChildProducts(true);
            const query = new URLSearchParams({
                parentProductId: productId,
            }).toString();
            const url = new URL(`/api/products/?${query}`, process.env.APP_URL);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error('Something went wrong.');
            }
            const data = await response.json();
            if (data.error) {
                throw new Error(data.error);
            }
            setChildProducts(
                buildProducts(data.products)
            );
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingChildProducts(false);
        }
    }, [productId]);
    
    // Fetch child products on mount
    useEffect(() => {
        fetchChildProducts();
    }, [fetchChildProducts]);

    // Render nothing if loading or no child products
    if (loadingChildProducts
        || !childProducts?.length
    ) {
        return null;
    }

    // Render child products table
    return (<>
        <Table
            className={'mb-4 mt-2'}
            bordered>
            <thead>
                <tr>
                    <th>Item#</th>
                    <th>Item Name</th>
                    <th>Our Price</th>
                    <th>Qty</th>
                    <th>Add</th>
                </tr>
            </thead>
            <tbody>
                {childProducts.map((childProduct) => (
                    <ProductChildProduct
                        key={childProduct.id}
                        childProductId={childProduct.id}
                        childProductName={childProduct.title}
                        childProductPrice={childProduct.price}
                        childProductQty={childProduct.availableQuantity}
                        inventoryLevelId={childProduct.inventoryLevelId}
                        maxQty={childProduct.availableQuantity}
                        addToCart={handleAddToCart}
                        isAddedToCart={addedProducts
                            .some((product) => product.inventoryLevelId === childProduct.inventoryLevelId)}
                    />
                ))}
            </tbody>
            <td
                style={{
                    textAlign: 'right',
                }}
                colSpan={5}>
                <p
                    className={'mb-0'}>Check the items you wish to purchase, then click</p>
                <Button
                    style={{
                        borderRadius: '0',
                    }}
                    className={'mt-2 mb-2 justify-content-end'}
                    onClick={handleClick}
                    disabled={(cartUpdating || !addedProducts.length)}
                    variant={'primary'}>
                    {cartUpdating ? 'Updating Cart...' : 'Add to Cart'}
                </Button>
            </td>
        </Table>
    </>);
}

ProductChildProductsTable.propTypes = {
    productId: string.isRequired,
};